<template>
  <main class="create-mnemonic-page">
    <AppNavBar
      :title="t('auth.backupMnemonic')"
      :border="false"
      background-color="#f5f5f5"
    />

    <section class="create-mnemonic-body">
      <h1>{{ t('auth.backupWarnTitle') }}</h1>

      <div class="tip-row">
        <div v-for="item in tipItems" :key="item.key" class="tip-item">
          <app-image :src="item.ok ? circleYesIcon : circleXIcon" alt="" />
          <span>{{ item.text }}</span>
        </div>
      </div>

      <p class="desc">{{ t('auth.mnemonicDesc') }}</p>

      <div class="mnemonic-grid">
        <div v-for="(word, index) in mnemonicList" :key="index" class="mnemonic-item">
          <span class="index">{{ index + 1 }}</span>
          <span class="word">{{ word }}</span>
        </div>
      </div>
    </section>

    <footer class="create-mnemonic-footer">
      <AuthThemeButton block @click="goVerify">
        {{ t('auth.backupMnemonic') }}
      </AuthThemeButton>
    </footer>
  </main>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { showFailToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';
import { useProfileStore } from '@/stores/profile';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import { getRecoverableMnemonic } from '@/utils/walletSession';
import circleYesIcon from '@/assets/images/circle_yes_icon.png';
import circleXIcon from '@/assets/images/circle_x_icon.png';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, 'auth-backup-mnemonic', {}));

const isExport = computed(() => String(pageState.value.export || '') === '1');
const mnemonic = computed(() => {
  if (pageState.value.mnemonic) return String(pageState.value.mnemonic);
  return getRecoverableMnemonic(profileStore.accountStorageAddress);
});
const mnemonicList = computed(() => mnemonic.value.split(' ').filter(Boolean));

const tipItems = [
  { key: 'ok', ok: true, text: t('auth.backupTipWrite') },
  { key: 'no-copy', ok: false, text: t('auth.backupTipNoCopy') },
  { key: 'no-screenshot', ok: false, text: t('auth.backupTipNoScreenshot') }
];

function goVerify() {
  if (!mnemonic.value) {
    showFailToast(t('auth.mnemonicUnavailable'));
    return;
  }

  navigateWithState(router, '/auth/verify-mnemonic', {
    namespace: 'auth-verify-mnemonic',
    payload: {
      export: isExport.value ? '1' : '0'
    }
  });
}

onMounted(() => {
  if (!mnemonic.value) {
    showFailToast(t('auth.mnemonicUnavailable'));
  }
});
</script>

<style scoped lang="scss">
.create-mnemonic-page {
  display: flex;
  flex-direction: column;
  min-height: var(--app-viewport-height);
  background: #f5f5f5;
}

.create-mnemonic-body {
  flex: 1;
  padding: 0 14px 20px;

  h1 {
    margin: 0;
    color: #000000;
    font-size: 22px;
    font-weight: 700;
  }
}

.tip-row {
  display: flex;
  gap: 8px;
  margin-top: 21px;
}

.tip-item {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;

  .app-image {
    width: 14px;
    height: 14px;
    margin-right: 5px;
    flex-shrink: 0;
  }

  span {
    color: #666666;
    font-size: 12px;
    line-height: 1.4;
  }
}

.desc {
  margin: 21px 0;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  line-height: 1.6;
}

.mnemonic-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 14px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.mnemonic-item {
  display: flex;
  align-items: center;
  width: calc(50% - 6px);
  margin-bottom: 10px;
  padding: 11px;
  border: 1px solid rgba(120, 120, 120, 0.28);
  border-radius: 16px;
  background: #f6f6f6;

  .index {
    min-width: 16px;
    color: #666666;
    font-size: 14px;
  }

  .word {
    margin-left: 5px;
    color: #000000;
    font-size: 14px;
  }
}

.create-mnemonic-footer {
  padding: 0 14px 30px;
}
</style>
